import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { checkCooldown, formatCooldownTime } from '../../../utils/eventCooldown';
import { EventCardIcon } from '../../discover/EventCardIcons';
import './EventUsersActionBar.css';

/**
 * Komponent Action Bar - pasek z przyciskiem dołącz i informacją o statusie
 */
function EventUsersActionBar({
  event,
  userApplication,
  isParticipant,
  participantsCount,
  maxParticipants,
  onJoin,
  onWithdraw
}) {
  const { t } = useTranslation('events');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Automatyczne zmniejszanie cooldown z czasem
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const interval = setInterval(() => {
      const lastActionTime = isParticipant 
        ? null // Dla uczestników sprawdzamy osobno
        : userApplication?.updated_at;
      
      const { secondsRemaining } = checkCooldown(lastActionTime);
      setCooldownRemaining(secondsRemaining);

      if (secondsRemaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownRemaining, isParticipant, userApplication]);

  // Oblicz dostępne miejsca
  const availableSpots = maxParticipants - participantsCount;
  const isFull = availableSpots <= 0;
  const messageLimit = 500;

  // Określ status użytkownika
  const getStatus = () => {
    if (isParticipant) {
      return 'joined';
    }
    if (userApplication?.status === 'pending') {
      return 'pending';
    }
    if (userApplication?.status === 'rejected') {
      return 'rejected';
    }
    if (userApplication?.status === 'withdrawn') {
      return 'withdrawn';
    }
    return 'none';
  };

  const status = getStatus();

  const handleJoinClick = async () => {
    // Sprawdź cooldown
    const lastActionTime = userApplication?.updated_at;
    const { canPerform, secondsRemaining } = checkCooldown(lastActionTime);
    
    if (!canPerform) {
      alert(t('events.join.cooldown', { seconds: formatCooldownTime(secondsRemaining) }));
      setCooldownRemaining(secondsRemaining);
      return;
    }

    setLoading(true);
    try {
      await onJoin(message);
      setMessage(''); // Wyczyść wiadomość po dołączeniu
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawClick = async () => {
    // Sprawdź cooldown
    const lastActionTime = isParticipant 
      ? null // Dla uczestników sprawdzamy osobno
      : userApplication?.updated_at;
    
    const { canPerform, secondsRemaining } = checkCooldown(lastActionTime);
    
    if (!canPerform) {
      alert(t('events.leave.cooldown', { seconds: formatCooldownTime(secondsRemaining) }));
      setCooldownRemaining(secondsRemaining);
      return;
    }

    setLoading(true);
    try {
      await onWithdraw();
    } finally {
      setLoading(false);
    }
  };

  const barMods = [
    'event-users-action-bar',
    status === 'pending' && 'event-users-action-bar--pending',
    status === 'joined' && 'event-users-action-bar--joined',
    isFull && 'event-users-action-bar--full',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={barMods}>
      <div className="event-users-action-bar-shine" aria-hidden />

      {/* Status informacja */}
      <div className="event-users-action-bar-status">
        <div className="event-users-action-bar-status-info">
          <span className="event-users-action-bar-participants">
            <EventCardIcon type="users" className="event-users-action-bar-participants-icon" size={18} />
            <span className="event-users-action-bar-participants-text">
              {participantsCount}/{maxParticipants} {t('events.page.footer.placesLeft', { count: availableSpots })}
            </span>
          </span>
          {isFull && (
            <span className="event-users-action-bar-full-badge">
              <EventCardIcon type="alert" className="event-users-action-bar-full-badge-icon" size={14} />
              {t('events.applications.eventFull')}
            </span>
          )}
        </div>
        {status === 'pending' && (
          <p className="event-users-action-bar-pending-hint event-users-action-bar-pending-hint--top">
            {t('events.join.pendingHint')}
          </p>
        )}
      </div>

      {/* Przyciski akcji */}
      <div className="event-users-action-bar-actions">
        {status === 'joined' && (
          <button
            type="button"
            className="event-users-action-bar-button leave-button"
            onClick={handleWithdrawClick}
            disabled={loading || cooldownRemaining > 0}
          >
            <EventCardIcon type="participant" className="event-users-action-bar-button-icon" size={17} />
            {loading ? t('events.leave.leaving') : t('events.leave.button')}
          </button>
        )}

        {status === 'pending' && (
          <div className="event-users-action-bar-pending">
            <div className="event-users-action-bar-pending-track">
              <span
                className="event-users-action-bar-pill pending-pill"
                role="status"
                aria-label={t('events.join.pending')}
              >
                <EventCardIcon type="hourglass" className="event-users-action-bar-pill-icon" size={16} />
                {t('events.join.pending')}
              </span>
              <button
                type="button"
                className="event-users-action-bar-button withdraw-button"
                onClick={handleWithdrawClick}
                disabled={loading || cooldownRemaining > 0}
              >
                <EventCardIcon type="clipboard" className="event-users-action-bar-button-icon" size={16} />
                {t('events.join.withdraw')}
              </button>
            </div>
          </div>
        )}

        {(status === 'none' || status === 'rejected' || status === 'withdrawn') && (
          <div className="event-users-action-bar-join">
            {!isFull && (
              <div className="event-users-action-bar-message-wrap">
                <input
                  type="text"
                  className="event-users-action-bar-message-input"
                  placeholder={t('events.join.messagePlaceholder')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={messageLimit}
                  disabled={loading || cooldownRemaining > 0}
                />
                <span
                  className="event-users-action-bar-message-counter"
                  aria-live="polite"
                >
                  {message.length}/{messageLimit}
                </span>
              </div>
            )}
            <button
              type="button"
              className="event-users-action-bar-button join-button"
              onClick={handleJoinClick}
              disabled={loading || isFull || cooldownRemaining > 0}
            >
              <EventCardIcon type="users" className="event-users-action-bar-button-icon" size={17} />
              {loading ? t('events.join.joining') : t('events.join.button')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventUsersActionBar;
